import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { AccountDataService } from 'src/shared/services/account.data.service/account.data.service';
import { environment } from 'src/environments/environment';
import { StaffProfileDataService } from 'src/pms/services/staffprofiles.data.service/staffprofiles.data.service';
import { ClinicAuthDataService } from 'src/trialsClinic/services/clinic-auth.data.service';
import { AuthenticationService } from '../services/auth/auth_service';
import { CheckAccessDataService } from '../services/checkaccess.data.service/checkaccess.data.service';
import { CookieService } from '../services/cookies/cookie_service';
import { checkEmailVerified } from './emailVerified.guard';

/**
 * Redirects to the login page if the user is not logged in.
 * Redirects to the onboarding page if the user is not onboarded.
 * @param route
 * @param state
 * @returns boolean
 */
export const LoginGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthenticationService);
  const cookieService = inject(CookieService);
  const accountDataService = inject(AccountDataService);
  const staffProfileDataService = inject(StaffProfileDataService);
  const router = inject(Router);
  const clinicAuthDataService = inject(ClinicAuthDataService);
  const destroyRef = inject(DestroyRef);
  const checkAccessDataService = inject(CheckAccessDataService);

  const tenants = authService.getAllTenants();
  const currentUrl = state.url;
  const onboardingRedirectUrl = environment.onboarding_route;
  const app = environment.app;

  // Check if the user is onboarded on the App.
  const checkAppOnboarding = (currentUrl: string, redirectUrl: string) => {
    return of(currentUrl).pipe(
      switchMap(() => {
        return accountDataService.GetMyUser().pipe(
          catchError((err) => of(err)),
          map((response: any) => {
            if (response.code && response.code == 'failed_precondition') {
              router.navigate([redirectUrl], {
                queryParams: { redirectTo: `${currentUrl}` },
                queryParamsHandling: 'merge',
              });
              return false;
            } else {
              return true;
            }
          })
        );
      })
    );
  };

  const pmsUrlPath = (url: string) => url.split('?')[0];

  // Check if the user is onboarded on the PMS.
  const checkPMSOnboarding = (currentUrl: string, redirectUrl: string) => {
    if (pmsUrlPath(currentUrl) === '/checkaccess') {
      return of(true);
    }

    return from(checkAccessDataService.ensureRolesReady()).pipe(
      switchMap(() => {
        if (!checkAccessDataService.hasRole('clinician')) {
          if (currentUrl === '/not-clinician') {
            return of(true);
          }
          router.navigate(['/not-clinician'], {
            queryParams: { redirectTo: `${currentUrl}` },
            queryParamsHandling: 'merge',
          });
          return of(false);
        }

        return staffProfileDataService.GetMyStaffProfile().pipe(
          catchError((err) => of(err)),
          map((response: any) => {
            if (response?.code === 'not_found') {
              router.navigate([redirectUrl], {
                queryParams: { redirectTo: `${currentUrl}` },
                queryParamsHandling: 'merge',
              });
              return false;
            }
            return true;
          })
        );
      })
    );
  };
  const checkTrialsClinicOnboarding = (currentUrl: string) => {
    if (currentUrl === '/nouser') {
      return of(true);
    }
    return clinicAuthDataService.getMyClinicProfile().pipe(
      map(() => true),
      takeUntilDestroyed(destroyRef),
      catchError(() =>
        clinicAuthDataService.verifyAndLink().pipe(
          takeUntilDestroyed(destroyRef),
          catchError((err: any) => {
            if (err?.code === 'not_found') {
              router.navigate(['/nouser']);
              return of(false);
            }
            return of(err);
          }),
          map((response: any) => {
            if (response === false) {
              return false;
            }
            return true;
          })
        )
      )
    );
  };

  return of(cookieService.getCookie('loggedIn')).pipe(
    switchMap((loggedIn) => {
      if (!loggedIn) {
        const app = authService.getApp();
        const url = app === 'Portal' ? '/login' : tenants?.length > 1 ? '/tenants/login' : '/login';
        router.navigate([url], {
          queryParams: { redirectTo: `${state.url}` },
        });
        return of(false);
      }

      authService.setSentryUser();

      switch (app) {
        case 'PMS':
          return checkPMSOnboarding(currentUrl, onboardingRedirectUrl);
        case 'trials':
          return checkAppOnboarding(currentUrl, onboardingRedirectUrl).pipe(
            switchMap((result) => {
              if (result !== true) return of(result);
              return from(checkEmailVerified(authService, router, state));
            })
          );
        case 'trialsClinic':
          return checkTrialsClinicOnboarding(currentUrl).pipe(
            switchMap((result) => {
              if (result !== true) return of(result);
              // Skip email verification for /nouser - user has no clinic profile yet
              if (currentUrl === '/nouser') return of(true);
              return from(checkEmailVerified(authService, router, state));
            })
          );
        default:
          return of(true);
      }
    })
  );
};
